import { useNavigate, useParams } from 'react-router'
import React, { useEffect, useMemo, useState } from 'react'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
  getImageLink,
} from 'src/mentor/helpers/utilityFunctions'
import { getMinimalMentorData } from 'src/supportPortal/views/therapists/util'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Avatar,
  Card,
  CardContent,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Button, styled } from '@mui/material'
import Person from '@mui/icons-material/Person'
import Business from '@mui/icons-material/Business'
import LocationOn from '@mui/icons-material/LocationOn'
import useServerAuth from 'src/customHooks/useServerAuth'

const Container = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(3, 2),
  background: 'linear-gradient(180deg, rgba(249, 250, 251, 0.5) 0%, rgba(243, 244, 246, 0.3) 100%)',
  minHeight: 'calc(100vh - 200px)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 1.5),
  },
}))

const ProfileCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: '1400px',
  margin: '0 auto',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
  background: 'linear-gradient(135deg, rgba(216, 101, 0, 0.05) 0%, rgba(216, 101, 0, 0.02) 100%)',
  border: '1px solid rgba(216, 101, 0, 0.1)',
  [theme.breakpoints.down('sm')]: {
    borderRadius: '12px',
    marginBottom: theme.spacing(2),
  },
}))

const BrandAccordion = styled(Accordion)(({ theme }) => ({
  width: '100%',
  maxWidth: '1400px',
  margin: '0 auto',
  marginBottom: theme.spacing(2),
  borderRadius: '16px !important',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05) !important',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  overflow: 'hidden',
  '&:before': {
    display: 'none',
  },
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08) !important',
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: '12px !important',
    marginBottom: theme.spacing(1.5),
  },
}))

const AccordionHeader = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  minHeight: '72px !important',
  '&.Mui-expanded': {
    minHeight: '72px !important',
    backgroundColor: 'rgba(216, 101, 0, 0.03)',
  },
  '& .MuiAccordionSummary-content': {
    margin: 0,
    alignItems: 'center',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5, 2),
    minHeight: '64px !important',
    '&.Mui-expanded': {
      minHeight: '64px !important',
    },
  },
}))

const ClinicListItem = styled(ListItemButton)(({ theme }) => ({
  margin: theme.spacing(1, 0),
  padding: theme.spacing(1.5, 2),
  borderRadius: '12px',
  background: 'rgba(0, 0, 0, 0.02)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'rgba(216, 101, 0, 0.08)',
    borderColor: 'rgba(216, 101, 0, 0.2)',
    transform: 'translateX(4px)',
    boxShadow: '0 2px 8px rgba(216, 101, 0, 0.15)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.25, 1.5),
    borderRadius: '10px',
  },
}))

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: '8px',
  fontWeight: 500,
  fontSize: '0.875rem',
  padding: theme.spacing(0.75, 1.5),
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1.25),
    minWidth: 'auto',
  },
}))

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(8),
  width: '100%',
}))

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(8),
  color: 'text.secondary',
  width: '100%',
  maxWidth: '1400px',
  margin: '0 auto',
}))

export default function MentorClinicAssociations({
  getAssociatedBrandsApiSignature = '/api/secure/cc/support/therapists/clinic-associations',
  getSelectedMentorApiSignature = '/api/secure/cc/support/therapists/by-slug',
  headingOverride,
  subComponent,
  disableClinicOnClick,
  hidePayoutReports = false,
  hideEngagementModels = false,
  hidePayoutInvoices = false,
  showScopedLaunchers = false,
  scopedActionEntityType = 'therapists',
}) {
  const navigate = useNavigate()
  const { mentorSlug } = useParams()
  const { allRolesInBoolean } = useServerAuth()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const ListItemComponent = disableClinicOnClick ? ListItem : ClinicListItem

  const [associatedClinics, setAssociatedClinics] = useState([])
  const [selectedTherapist, setSelectedTherapist] = useState()
  const [isLoading, setIsLoading] = useState(true)
  const [entityFeeModels, setEntityFeeModels] = useState([])

  const uniqueBrands = useMemo(() => {
    return [
      ...new Map(
        associatedClinics?.map((item) => [
          item?.clinicBrandId,
          {
            id: item?.clinicBrandId,
            title: item?.clinicBrandName,
            ownerUserIds: item?.clinicBrandOwnerUserIds,
            slug: item?.clinicBrandSlug,
          },
        ]),
      ).values(),
    ]
  }, [associatedClinics])

  const scopedDetailBasePath = useMemo(() => {
    if (!showScopedLaunchers || !mentorSlug) {
      return ''
    }
    return `/support-home/practitioner-management/${scopedActionEntityType}/${mentorSlug}`
  }, [mentorSlug, scopedActionEntityType, showScopedLaunchers])
  const canViewTherapistPayoutReports =
    scopedActionEntityType === 'therapists' && allRolesInBoolean.isTherapistAdmin

  async function getEntityFeeModels() {
    console.log('efmBrands', uniqueBrands)
    const brandIds = uniqueBrands?.map((brand) => brand?.id) || []
    if (!brandIds.length) {
      setEntityFeeModels([])
      return
    }

    const resp = await getSPDataWithParams(
      '/api/secure/cc/support/clinic/entity-fee-model',
      { 'clinic-brand-ids': brandIds.join(',') },
      undefined,
      undefined,
      true,
    )
    if (resp && isCommonResponseSuccessfulV2(resp)) {
      const dat = getDataMapFromCommonResponse(resp)?.['entity-fee-model']
      setEntityFeeModels(dat)
    }
  }

  async function getAssociatedBrands() {
    const resp = await getSPDataWithParams(
      getAssociatedBrandsApiSignature,
      { 'mentor-slug': mentorSlug },
      undefined,
      undefined,
      true,
    )
    if (resp && isCommonResponseSuccessfulV2(resp)) {
      const dat = getDataMapFromCommonResponse(resp)?.clinic

      console.log({ dat })
      setAssociatedClinics(dat)
    }
  }

  async function getSelectedTherapist() {
    if (!mentorSlug) {
      return
    }
    const resp = await getMinimalMentorData(mentorSlug, undefined, getSelectedMentorApiSignature)
    setSelectedTherapist(resp)
  }

  async function Setup() {
    setIsLoading(true)
    let promises = []
    if (getSelectedMentorApiSignature) {
      promises.push(getSelectedTherapist())
    }
    promises.push(getAssociatedBrands())
    await Promise.all(promises)
    setIsLoading(false)
  }

  function checkIfEntityFeeModelForBrandExists(brandId) {
    return entityFeeModels?.some((model) => model?.entityId === brandId)
  }

  function persistSupportContext(brand, clinicLocation = null) {
    if (selectedTherapist) {
      sessionStorage.setItem('supportMentorData', JSON.stringify(selectedTherapist))
    }
    if (brand) {
      sessionStorage.setItem('supportClinicBrandData', JSON.stringify(brand))
    }
    if (clinicLocation) {
      sessionStorage.setItem('supportClinicData', JSON.stringify(clinicLocation))
    } else {
      sessionStorage.removeItem('supportClinicData')
    }
  }

  function buildScopedQuery({ brand, clinicLocation = null, tab = null }) {
    const params = new URLSearchParams()
    if (tab) {
      params.set('tab', tab)
    }
    if (brand?.id) {
      params.set('clinicBrandId', brand.id)
    }
    if (clinicLocation?.id) {
      params.set('clinicLocationId', clinicLocation.id)
    }
    const queryString = params.toString()
    return queryString ? `?${queryString}` : ''
  }

  function openScopedEventsAndAppointments(brand, clinicLocation) {
    persistSupportContext(brand, clinicLocation)
    navigate(`${scopedDetailBasePath}/events${buildScopedQuery({ brand, clinicLocation, tab: 'events' })}`)
  }

  function openScopedBundlePacks(brand, clinicLocation) {
    persistSupportContext(brand, clinicLocation)
    navigate(`${scopedDetailBasePath}/events${buildScopedQuery({ brand, clinicLocation, tab: 'packs' })}`)
  }

  function openScopedPatients(brand) {
    persistSupportContext(brand)
    navigate(`${scopedDetailBasePath}/patients${buildScopedQuery({ brand })}`)
  }

  function openScopedPayoutReports(brand, clinicLocation = null) {
    persistSupportContext(brand, clinicLocation)
    navigate(`${scopedDetailBasePath}/payout-reports${buildScopedQuery({ brand, clinicLocation })}`)
  }

  useEffect(() => {
    Setup()
  }, [])

  useEffect(() => {
    if (!hidePayoutReports || !hideEngagementModels) {
      getEntityFeeModels()
    }
  }, [uniqueBrands])

  console.log({ uniqueBrands, associatedClinics })

  const clinicsForBrand = (brandId) => {
    return associatedClinics?.filter((clinic) => clinic?.clinicBrandId === brandId) || []
  }

  return (
    <>
      <SupportHeader
        heading={headingOverride || `${selectedTherapist?.name || 'Therapist'} - Clinics`}
        showBackButton
      />
      <Container>
        {isLoading ? (
          <LoadingContainer>
            <CircularProgress size={48} />
          </LoadingContainer>
        ) : (
          <>
            {/* Therapist Profile Card */}
            {selectedTherapist && (
              <ProfileCard>
                <CardContent
                  sx={{
                    padding: { xs: theme.spacing(2), sm: theme.spacing(2.5), md: theme.spacing(3) },
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing(2),
                  }}
                >
                  <Avatar
                    src={selectedTherapist?.profilePicId ? getImageLink(selectedTherapist?.profilePicId) : null}
                    sx={{
                      width: { xs: 64, sm: 80, md: 96 },
                      height: { xs: 64, sm: 80, md: 96 },
                      bgcolor: 'rgba(216, 101, 0, 0.1)',
                      border: '3px solid rgba(216, 101, 0, 0.2)',
                    }}
                  >
                    <Person sx={{ fontSize: { xs: 32, sm: 40, md: 48 } }} />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                        color: 'text.primary',
                        marginBottom: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {selectedTherapist?.name}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                      <Chip
                        icon={<Business sx={{ fontSize: '16px !important' }} />}
                        label={`${uniqueBrands?.length || 0} Clinic Brand${uniqueBrands?.length !== 1 ? 's' : ''}`}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(216, 101, 0, 0.1)',
                          color: 'rgba(216, 101, 0, 0.9)',
                          fontWeight: 500,
                        }}
                      />
                      <Chip
                        icon={<LocationOn sx={{ fontSize: '16px !important' }} />}
                        label={`${associatedClinics?.length || 0} Location${associatedClinics?.length !== 1 ? 's' : ''}`}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                          color: 'rgba(59, 130, 246, 0.9)',
                          fontWeight: 500,
                        }}
                      />
                    </Stack>
                  </Box>
                </CardContent>
              </ProfileCard>
            )}

            {/* Clinic Brands */}
            {uniqueBrands?.length === 0 && !isLoading && (
              <EmptyState>
                <Business sx={{ fontSize: 64, color: 'text.disabled', marginBottom: 2 }} />
                <Typography variant="h6" sx={{ marginBottom: 1 }}>
                  No Clinic Associations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This therapist is not associated with any clinics yet.
                </Typography>
              </EmptyState>
            )}

            <Stack spacing={2} sx={{ width: '100%' }}>
              {uniqueBrands
                ?.sort((a, b) => a.title?.localeCompare(b.title))
                ?.map((brand) => {
                  const clinics = clinicsForBrand(brand?.id)
                  return (
                    <BrandAccordion key={brand?.id} defaultExpanded={!isMobile}>
                      <AccordionHeader expandIcon={<ExpandMoreIcon />}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            justifyContent: 'space-between',
                            width: '100%',
                            gap: { xs: 1.5, sm: 2 },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                            <Business
                              sx={{
                                color: 'rgba(216, 101, 0, 0.7)',
                                fontSize: { xs: 24, sm: 28 },
                              }}
                            />
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {brand?.title}
                            </Typography>
                            <Chip
                              label={`${clinics.length} Location${clinics.length !== 1 ? 's' : ''}`}
                              size="small"
                              sx={{
                                height: '24px',
                                fontSize: '0.75rem',
                                backgroundColor: 'rgba(216, 101, 0, 0.1)',
                                color: 'rgba(216, 101, 0, 0.9)',
                                fontWeight: 500,
                              }}
                            />
                          </Box>
                          {((!hidePayoutReports && canViewTherapistPayoutReports) ||
                            !hideEngagementModels ||
                            !hidePayoutInvoices ||
                            showScopedLaunchers) && (
                            <Stack
                              direction="row"
                              spacing={1}
                              flexWrap="wrap"
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                gap: { xs: 0.75, sm: 1 },
                                width: { xs: '100%', sm: 'auto' },
                              }}
                            >
                              {showScopedLaunchers && (
                                <>
                                  <ActionButton
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => openScopedPatients(brand)}
                                    sx={{
                                      flex: { xs: '1 1 auto', sm: '0 0 auto' },
                                      minWidth: { xs: '120px', sm: 'auto' },
                                    }}
                                  >
                                    Patients
                                  </ActionButton>
                                  {canViewTherapistPayoutReports && (
                                    <ActionButton
                                      variant="outlined"
                                      color="primary"
                                      disabled={!checkIfEntityFeeModelForBrandExists(brand?.id)}
                                      onClick={() => openScopedPayoutReports(brand)}
                                      sx={{
                                        flex: { xs: '1 1 auto', sm: '0 0 auto' },
                                        minWidth: { xs: '120px', sm: 'auto' },
                                      }}
                                    >
                                      {!isMobile && 'Payout Reports'}
                                      {isMobile && 'Reports'}
                                    </ActionButton>
                                  )}
                                </>
                              )}
                              {!hidePayoutReports && canViewTherapistPayoutReports && (
                                <ActionButton
                                  variant="outlined"
                                  color="primary"
                                  disabled={!checkIfEntityFeeModelForBrandExists(brand?.id)}
                                  onClick={() => {
                                    navigate(`${brand?.slug}/payout-reports`)
                                  }}
                                  sx={{
                                    flex: { xs: '1 1 auto', sm: '0 0 auto' },
                                    minWidth: { xs: '120px', sm: 'auto' },
                                  }}
                                >
                                  {!isMobile && 'Payout Reports'}
                                  {isMobile && 'Reports'}
                                </ActionButton>
                              )}
                              {!hideEngagementModels && (
                                <ActionButton
                                  variant="outlined"
                                  color="primary"
                                  disabled={!checkIfEntityFeeModelForBrandExists(brand?.id)}
                                  onClick={() => {
                                    navigate(`${brand?.slug}/engagement-models`)
                                  }}
                                  sx={{
                                    flex: { xs: '1 1 auto', sm: '0 0 auto' },
                                    minWidth: { xs: '120px', sm: 'auto' },
                                  }}
                                >
                                  {!isMobile && 'Engagement Models'}
                                  {isMobile && 'Models'}
                                </ActionButton>
                              )}
                              {!hidePayoutInvoices && (
                                <ActionButton
                                  variant="outlined"
                                  color="primary"
                                  onClick={() => {
                                    navigate(`${brand?.slug}/payout-invoices`)
                                  }}
                                  sx={{
                                    flex: { xs: '1 1 auto', sm: '0 0 auto' },
                                    minWidth: { xs: '120px', sm: 'auto' },
                                  }}
                                >
                                  {!isMobile && 'Payout Invoices'}
                                  {isMobile && 'Invoices'}
                                </ActionButton>
                              )}
                            </Stack>
                          )}
                        </Box>
                      </AccordionHeader>
                      <AccordionDetails
                        sx={{
                          padding: { xs: theme.spacing(1.5, 2), sm: theme.spacing(2, 2.5) },
                          paddingTop: { xs: theme.spacing(1), sm: theme.spacing(1.5) },
                        }}
                      >
                        {clinics.length === 0 ? (
                          <Box
                            sx={{
                              textAlign: 'center',
                              padding: theme.spacing(4),
                              color: 'text.secondary',
                            }}
                          >
                            <Typography variant="body2">No clinics found for this brand.</Typography>
                          </Box>
                        ) : (
                          <List sx={{ padding: 0 }}>
                            {clinics.map((loc) => {
                              return (
                                <ListItemComponent
                                  key={loc?.id}
                                  onClick={() => {
                                    if (!disableClinicOnClick) {
                                      navigate(loc?.slug)
                                      sessionStorage.setItem('supportClinicData', JSON.stringify(loc))
                                    }
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: { xs: 'column', md: 'row' },
                                      alignItems: { xs: 'flex-start', md: 'center' },
                                      justifyContent: 'space-between',
                                      gap: { xs: 1.5, md: 2 },
                                      width: '100%',
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        minWidth: 0,
                                        width: '100%',
                                        flex: 1,
                                      }}
                                    >
                                      <LocationOn
                                        sx={{
                                          color: 'rgba(216, 101, 0, 0.6)',
                                          marginRight: 1.5,
                                          fontSize: { xs: 20, sm: 22 },
                                          flexShrink: 0,
                                        }}
                                      />
                                      <ListItemText
                                        primary={loc?.title}
                                        primaryTypographyProps={{
                                          sx: {
                                            fontWeight: 500,
                                            fontSize: { xs: '0.9375rem', sm: '1rem' },
                                          },
                                        }}
                                      />
                                    </Box>
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      flexWrap="wrap"
                                      onClick={(e) => e.stopPropagation()}
                                      sx={{
                                        gap: { xs: 0.75, sm: 1 },
                                        width: { xs: '100%', md: 'auto' },
                                      }}
                                    >
                                      {showScopedLaunchers && (
                                        <>
                                          <ActionButton
                                            variant="contained"
                                            color="primary"
                                            onClick={() => openScopedEventsAndAppointments(brand, loc)}
                                            sx={{
                                              flex: { xs: '1 1 auto', md: '0 0 auto' },
                                            }}
                                          >
                                            {isMobile ? 'Events' : 'Events & Appointments'}
                                          </ActionButton>
                                          <ActionButton
                                            variant="outlined"
                                            color="secondary"
                                            onClick={() => openScopedBundlePacks(brand, loc)}
                                            sx={{
                                              flex: { xs: '1 1 auto', md: '0 0 auto' },
                                            }}
                                          >
                                            Bundle Packs
                                          </ActionButton>
                                          {canViewTherapistPayoutReports && (
                                            <ActionButton
                                              variant="outlined"
                                              color="primary"
                                              disabled={!checkIfEntityFeeModelForBrandExists(brand?.id)}
                                              onClick={() => openScopedPayoutReports(brand, loc)}
                                              sx={{
                                                flex: { xs: '1 1 auto', md: '0 0 auto' },
                                              }}
                                            >
                                              {!isMobile && 'Payout Reports'}
                                              {isMobile && 'Reports'}
                                            </ActionButton>
                                          )}
                                        </>
                                      )}
                                      {subComponent && subComponent(loc)}
                                    </Stack>
                                  </Box>
                                </ListItemComponent>
                              )
                            })}
                          </List>
                        )}
                      </AccordionDetails>
                    </BrandAccordion>
                  )
                })}
            </Stack>
          </>
        )}
      </Container>
    </>
  )
}
